import { useState, useRef } from "react";
import { Flag, Paperclip, Trash2, Mail, MailOpen, KeyRound } from "lucide-react";
import { Email, useEmailStore } from "../../stores/emailStore";
import { useLabelStore } from "../../stores/labelStore";
import { useContextMenuStore } from "../../stores/contextMenuStore";
import { useApi } from "../../hooks/useApi";
import { formatRelativeDate, formatFileSize } from "../../utils/formatDate";
import { deleteEmailsWithUndo } from "../../utils/deleteWithUndo";
import { detectVerificationCode } from "../../utils/otpDetector";

interface EmailListItemProps {
  email: Email;
  isSelected: boolean;
  onOpenDraft?: (email: Email) => void;
}

export default function EmailListItem({ email, isSelected, onOpenDraft }: EmailListItemProps) {
  const {
    setCurrentEmail,
    toggleSelectEmail,
    selectedEmailIds,
    markAsRead,
    markAsUnread,
  } = useEmailStore();
  const labels = useLabelStore((s) => s.labels);
  const { del, patch } = useApi();

  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const isSwipingRef = useRef(false);
  const assignedLabels = labels.filter((l) => email.labels?.includes(l.id));
  const primaryLabel = assignedLabels[0];

  const handleSelectEmail = () => {
    if (!email.isRead) {
      markAsRead([email.id]);
      patch(`/mail/mails/${encodeURIComponent(email.folderId || "INBOX")}/${email.id}/flags`, {
        read: true,
      }).catch((err) => {
        console.error("Error updating read status:", err);
      });
    }
    setCurrentEmail({ ...email, isRead: true });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    isSwipingRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const diffX = e.touches[0].clientX - touchStartXRef.current;
    const diffY = e.touches[0].clientY - touchStartYRef.current;

    // Solo considerar swipe si el desplazamiento horizontal es mayor al vertical
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 15) {
      isSwipingRef.current = true;
      // Limitar rango de deslizamiento visual (-120px a 120px)
      const clamped = Math.max(-120, Math.min(120, diffX));
      setSwipeOffset(clamped);
    }
  };

  const handleTouchEnd = async () => {
    if (isSwipingRef.current) {
      if (swipeOffset < -70) {
        // Deslizar izquierda -> Mover a papelera (con opción de Deshacer)
        deleteEmailsWithUndo([email], () =>
          del(`/mail/mails/${encodeURIComponent(email.folderId || "INBOX")}/${email.id}`).catch((err) =>
            console.error("Failed to delete email via swipe:", err)
          )
        );
      } else if (swipeOffset > 70) {
        // Deslizar derecha -> Alternar leído / no leído
        const newReadStatus = !email.isRead;
        if (newReadStatus) {
          markAsRead([email.id]);
        } else {
          markAsUnread([email.id]);
        }
        try {
          await patch(`/mail/mails/${encodeURIComponent(email.folderId || "INBOX")}/${email.id}/flags`, {
            read: newReadStatus,
          });
        } catch (err) {
          console.error("Failed to toggle read via swipe:", err);
        }
      }
    }

    touchStartXRef.current = null;
    touchStartYRef.current = null;
    isSwipingRef.current = false;
    setSwipeOffset(0);
  };

  const openContextMenu = useContextMenuStore((s) => s.openMenu);
  const emails = useEmailStore((s) => s.emails);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // If right-clicked item is part of selected emails, pass all selected emails; otherwise pass target email
    const isTargetSelected = selectedEmailIds.has(email.id);
    const selectedList = isTargetSelected
      ? emails.filter((em) => selectedEmailIds.has(em.id))
      : [email];

    openContextMenu({ x: e.clientX, y: e.clientY }, email, selectedList);
  };

  const handleDragStart = (e: React.DragEvent) => {
    // Si hay varios correos seleccionados, enviar la lista de seleccionados
    const ids = selectedEmailIds.has(email.id)
      ? Array.from(selectedEmailIds)
      : [email.id];

    e.dataTransfer.setData("application/json", JSON.stringify({
      emailIds: ids,
      sourceFolder: email.folderId || "INBOX",
    }));
    e.dataTransfer.effectAllowed = "move";
  };

  return (
    <div
      onContextMenu={handleContextMenu}
      className="relative w-full max-w-full overflow-hidden select-none border-b border-[var(--border-subtle)] box-border"
    >
      {/* Fondo de acción al deslizar a la derecha (Marcar leído / no leído) */}
      <div
        className={`absolute inset-y-0 left-0 flex items-center px-4 transition-colors ${
          swipeOffset > 0 ? "bg-accent/20 text-accent" : "opacity-0"
        }`}
        style={{ width: "100%" }}
      >
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
          {email.isRead ? <Mail size={16} /> : <MailOpen size={16} />}
          <span>{email.isRead ? "No leído" : "Leído"}</span>
        </div>
      </div>

      {/* Fondo de acción al deslizar a la izquierda (Eliminar) */}
      <div
        className={`absolute inset-y-0 right-0 flex items-center justify-end px-4 transition-colors ${
          swipeOffset < 0 ? "bg-red-500/20 text-red-500" : "opacity-0"
        }`}
        style={{ width: "100%" }}
      >
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
          <span>Eliminar</span>
          <Trash2 size={16} />
        </div>
      </div>

      {/* Elemento deslizable y arrastrable */}
      <div
        draggable
        onDragStart={handleDragStart}
        onClick={() => {
          if (!isSwipingRef.current) handleSelectEmail();
        }}
        onDoubleClick={() => onOpenDraft && onOpenDraft(email)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          transition: isSwipingRef.current ? "none" : "transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        className={`relative w-full max-w-full box-border flex items-start gap-2.5 px-3.5 py-2.5 cursor-grab active:cursor-grabbing transition-colors min-w-0 ${
          isSelected
            ? "bg-[var(--bg-tertiary)] font-medium"
            : !email.isRead
              ? "bg-[var(--bg-secondary)]"
              : "bg-[var(--bg-primary)] hover:bg-[var(--bg-secondary)]/50"
        }`}
      >
      {/* Franja vertical derecha con el color de la etiqueta asignada */}
      {primaryLabel && (
        <span
          className="absolute right-0 top-0 bottom-0 w-1 pointer-events-none"
          style={{
            backgroundColor: primaryLabel.color,
          }}
        />
      )}

      {/* Checkbox */}
      <input
        type="checkbox"
        checked={selectedEmailIds.has(email.id)}
        onChange={(e) => {
          e.stopPropagation();
          toggleSelectEmail(email.id);
        }}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 accent-accent flex-shrink-0"
      />

      {/* Content */}
      <div className="flex-1 min-w-0 pr-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          {(() => {
            const isSentOrDraft =
              email.folderId?.toLowerCase().includes("sent") ||
              email.folderId?.toLowerCase().includes("enviad") ||
              email.folderId?.toLowerCase().includes("draft") ||
              email.folderId?.toLowerCase().includes("borrador");

            if (isSentOrDraft) {
              const toDisplay =
                email.to && email.to.length > 0
                  ? email.to.map((r) => r.name || r.email).join(", ")
                  : "Destinatario";
              return (
                <span
                  className={`text-sm sm:text-xs truncate min-w-0 flex-1 flex items-center gap-1 ${
                    !email.isRead
                      ? "font-bold text-[var(--text-primary)]"
                      : "text-[var(--text-secondary)]"
                  }`}
                >
                  <span className="text-[var(--text-muted)] font-normal text-xs sm:text-2xs uppercase tracking-wide flex-shrink-0">Para:</span>
                  <span className="truncate min-w-0">{toDisplay}</span>
                </span>
              );
            }

            return (
              <span
                className={`text-sm sm:text-xs truncate min-w-0 flex-1 ${
                  !email.isRead
                    ? "font-bold text-[var(--text-primary)]"
                    : "text-[var(--text-secondary)]"
                }`}
              >
                {email.from.name || email.from.email}
              </span>
            );
          })()}
          <span className="ml-2 text-xs sm:text-2xs text-[var(--text-muted)] flex-shrink-0 whitespace-nowrap">
            {formatRelativeDate(email.date)}
          </span>
        </div>

        {/* Fila del asunto con el tamaño y adjunto alineados a la derecha */}
        <div className="flex items-center justify-between gap-2">
          <p
            className={`text-sm truncate min-w-0 flex-1 ${
              !email.isRead
                ? "font-semibold text-[var(--text-primary)]"
                : "text-[var(--text-secondary)]"
            }`}
          >
            {email.subject}
          </p>
          <div className="flex items-center gap-1 text-2xs font-mono text-[var(--text-muted)] opacity-80 flex-shrink-0">
            {(email.hasAttachments || (email.attachments?.length ?? 0) > 0) && (
              <Paperclip size={11} className="text-[var(--text-secondary)]" />
            )}
            {email.size ? (
              <span>{formatFileSize(email.size)}</span>
            ) : null}
          </div>
        </div>

        {email.snippet && email.snippet.trim() !== email.subject.trim() && (
          <p className="text-xs text-[var(--text-muted)] truncate mt-0.5">
            {email.snippet}
          </p>
        )}

        {/* Labels, OTP badge and flags */}
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {(() => {
            const otp = detectVerificationCode(email.snippet || email.body || "", email.subject);
            if (!otp) return null;
            return (
              <span className="inline-flex items-center gap-1 text-2xs font-mono font-bold px-1.5 py-0.5 bg-accent/15 text-accent border border-accent/30 tracking-wider">
                <KeyRound size={10} />
                <span>CÓDIGO: {otp.code}</span>
              </span>
            );
          })()}
          {assignedLabels.map((label) => (
            <span
              key={label.id}
              className="inline-flex items-center gap-1 text-2xs font-bold px-1.5 py-0.5 text-white tracking-wide uppercase shadow-2xs"
              style={{
                backgroundColor: label.color,
              }}
            >
              {label.name}
            </span>
          ))}
          {email.isFlagged && (
            <Flag size={12} className="text-accent fill-accent" />
          )}
        </div>
      </div>
    </div>
  </div>
  );
}
